import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Upload, X, Paperclip } from "lucide-react";

const AREA_LABELS = { rgpd: "RGPD", prl: "Prevenció de Riscos", harassment: "Assetjament", appcc: "Seguretat Alimentària (APPCC)" };
const STATUS_LABELS = { compliant: "Complert ✓", pending: "Pendent ⏳", non_compliant: "Incomplert ✗", not_applicable: "No aplica" };
const PRIORITY_LABELS = { low: "Baixa", medium: "Mitjana", high: "Alta", critical: "Crítica" };

const BLANK = {
  title: "", description: "", area: "", status: "pending", priority: "medium",
  responsible_name: "", responsible_email: "", due_date: "", completed_date: "",
  review_period_months: 12, next_review_date: "", notes: "", evidence_urls: [], is_mandatory: true,
};

export default function ComplianceItemForm({ open, onClose, item, venueId, defaultArea, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({ ...BLANK, ...item });
    } else {
      setForm({ ...BLANK, area: defaultArea || "" });
    }
  }, [item, defaultArea, open]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const { file_url } = await hosys.integrations.Core.UploadFile({ file });
    set("evidence_urls", [...(form.evidence_urls || []), file_url]);
    setUploading(false);
  };

  const removeEvidence = (url) => {
    set("evidence_urls", (form.evidence_urls || []).filter(u => u !== url));
  };

  const handleSave = async () => {
    if (!form.title || !form.area) return;
    setSaving(true);
    if (item?.id) {
      await hosys.entities.ComplianceItem.update(item.id, { ...form, venue_id: venueId });
    } else {
      await hosys.entities.ComplianceItem.create({ ...form, venue_id: venueId });
    }
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? "Editar obligació" : "Nova obligació normativa"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Àrea normativa *</Label>
              <Select value={form.area} onValueChange={v => set("area", v)}>
                <SelectTrigger><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                <SelectContent>
                  {Object.entries(AREA_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prioritat</Label>
              <Select value={form.priority} onValueChange={v => set("priority", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Títol *</Label>
            <Input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Ex: Registre d'activitats de tractament" />
          </div>

          <div>
            <Label>Descripció</Label>
            <Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} placeholder="Descripció de l'obligació i com complir-la..." />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Estat</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Revisió periòdica (mesos)</Label>
              <Input type="number" value={form.review_period_months} onChange={e => set("review_period_months", Number(e.target.value))} min={0} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data límit</Label>
              <Input type="date" value={form.due_date} onChange={e => set("due_date", e.target.value)} />
            </div>
            <div>
              <Label>Data de compliment</Label>
              <Input type="date" value={form.completed_date} onChange={e => set("completed_date", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Responsable</Label>
              <Input value={form.responsible_name} onChange={e => set("responsible_name", e.target.value)} placeholder="Nom responsable" />
            </div>
            <div>
              <Label>Propera revisió</Label>
              <Input type="date" value={form.next_review_date} onChange={e => set("next_review_date", e.target.value)} />
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} placeholder="Observacions internes..." />
          </div>

          {/* Evidence files */}
          <div>
            <Label>Evidències documentals</Label>
            <div className="mt-1 space-y-1.5">
              {(form.evidence_urls || []).map((url, i) => (
                <div key={`url-${i}`} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                  <Paperclip className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 truncate flex-1 hover:underline">
                    {url.split("/").pop() || `Document ${i + 1}`}
                  </a>
                  <button onClick={() => removeEvidence(url)} className="text-gray-400 hover:text-red-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <label className="flex items-center gap-2 cursor-pointer bg-gray-50 border border-dashed border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Pujant fitxer..." : "Adjuntar evidència"}
                <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel·lar</Button>
          <Button onClick={handleSave} disabled={!form.title || !form.area || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {item ? "Actualitzar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}